import * as React from 'react';
import { Collapse, Container, Navbar, NavbarBrand, NavbarToggler, DropdownMenu, DropdownItem, DropdownToggle, Dropdown } from 'reactstrap';
import { Link, useHistory } from 'react-router-dom';
import './NavMenu.scss';
import { FeatureEnum, LoggedUser } from '../../models';
import MyNavItem from './MyNavItem';
import { RequireAdministrator, RequireProfessionalsManagement, RequirePricesManagement, RequirePermission, RequireClientsManagement, RequireAnalyticsPermission, RequireCampaignManagement, RequireAppointmentsManagement } from '../../components/RequirePermission';
import AccountCircle from "@material-ui/icons/AccountCircle";
import ExitToApp from "@material-ui/icons/ExitToApp";
import Settings from "@material-ui/icons/Settings";
import SupervisorAccountIcon from '@material-ui/icons/SupervisorAccount';
import Avatar from '@material-ui/core/Avatar';
import { TK } from '../../store/translations/translationKeys';
import { useTranslations } from '../../store/translations/hooks';
import AppNameIcon from '../../shared/icons/AppNameIcon';
import NotificationMenu from './NotificationsMenu';
import MessagesMenu from './MessagesMenu';
import TasksMenu from './TasksMenu/TasksMenu';
import AppIcon from '../../shared/icons/AppIcon';
import LocalAtmIcon from '@material-ui/icons/LocalAtm';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import CashRegisterMenu from './CashRegisterMenu/CashRegisterMenu';
import DashboardIcon from '@material-ui/icons/Dashboard';
import CampaignsIcon from '@material-ui/icons/Email';
import InventoryIcon from '@material-ui/icons/AllInbox';
import FormatListNumberedIcon from '@material-ui/icons/FormatListNumbered';
import ListIcon from '@material-ui/icons/List';
import { useDispatch, useSelector } from 'react-redux';
import { getFeatures, canAccessClientsChat } from '../../store/session/selectors';
import moment from 'moment';
import { paths } from '../constants';
import { getSettings } from '../../store/settings/selectors';
import { AutocompleteTextField } from '../../components/Forms/fields';
import { getSelectedBranch } from '../../store/general/selectors';
import { selectedBranchChange } from '../../store/general/actions';
import { getInitials } from '../../shared/string';
import { PermissionEnum } from '../../models';

export interface NavMenuStateProps {
    user?: LoggedUser,
}

export interface NavMenuDispatchProps {
    logout: () => void,
    homeClick: () => void,
}

export type NavMenuProps = NavMenuStateProps & NavMenuDispatchProps;



const NavMenu: React.FC<NavMenuProps> = (props) => {
    const { user, logout, homeClick } = props;
    
    const dispatch = useDispatch();
    const t = useTranslations();
    const history = useHistory();

    const features = useSelector(getFeatures);
    const settings = useSelector(getSettings);
    const selectedBranch = useSelector(getSelectedBranch);
    const hasClientsChat = useSelector(canAccessClientsChat);
    const canSeeTasksMenu = Boolean(user && !user.isClient && settings.taskManagement?.enabled);

    const canSeeCashRegisterMenu = Boolean(
        settings.cashRegisterSettings?.enabled &&
        user && !user.isClient && (
            user.permissions?.some(p => [
                PermissionEnum.Administrator,
                PermissionEnum.PricesAccess,
                PermissionEnum.PricesManagement,
            ].includes(p)) ||
            settings.cashRegisterSettings?.nonAdminApprovers?.includes(user.username ?? '')
        )
    );

    const [isOpen, setIsOpen] = React.useState(false);
    const [userDropdownOpen, setUserDropdown] = React.useState(false);
    const [userDropdownHovered, setUserDropdownHovered] = React.useState(false);

    const profileImageUrl = user?.photoUrl?.trim() || '';
    const [showProfileImage, setShowProfileImage] = React.useState(!!profileImageUrl);

    React.useEffect(() => {
        setShowProfileImage(!!profileImageUrl);
    }, [profileImageUrl]);

    const userBranchIds = React.useMemo(() => user?.branchIds ?? [], [user?.branchIds]);
    React.useEffect(() => {
        if (userBranchIds.length === 0) return;
        if (!selectedBranch || !userBranchIds.includes(selectedBranch)) {
            dispatch(selectedBranchChange(userBranchIds[0]));
        }
    }, [userBranchIds, selectedBranch]);

    const onBranchChange = (displayName: string) => {
        const branchId = Object.entries(settings.general?.branches ?? {}).find(([_, config]) => config.displayName === displayName)?.[0]
        if (branchId) {
            dispatch(selectedBranchChange(branchId))
        }
    }

    const userDropdownToggle = () => {
        // Only toggle if not currently hovered (prevents click from closing during hover)
        if (!userDropdownHovered) {
            setUserDropdown(!userDropdownOpen);
        }
    };
    const isLoggedIn = user;
    const isProfessional = !!user?.professionalId
    
    const daysToExpire = user ? moment.utc(user.accountExpiryDate ?? moment.now()).diff(moment.now(), 'days') : Infinity
    const paymentExpired = user?.isPaymentExpired === true

    return (
        <header>
            <Navbar
                className="navbar-expand-sm navbar-toggleable-sm border-bottom box-shadow mb-3 appNavbar" light fixed="top"
                style={{background: paymentExpired || daysToExpire < 15 ? '#F55' : undefined}}
            >
                {
                    paymentExpired ?
                        <span style={{color: '#f0e552', position: 'absolute', fontSize: '0.9rem', top: 0, textAlign: 'center', left: 0, right: 0}}>
                            {t(TK.subscriptionExpired)}
                        </span>
                    : daysToExpire < 20 ?
                        <span style={{color: daysToExpire < 15 ? '#f0e552' : '#fff', position: 'absolute', fontSize: '0.9rem', top: 0, textAlign: 'center', left: 0, right: 0}}>
                            {t(TK.yourAccountWillExpireIn)} {daysToExpire} {t(TK.days).toLocaleLowerCase()}
                        </span>
                    : user?.storageIsFull ?
                        <span style={{color: '#fff', position: 'absolute', fontSize: '0.9rem', top: 0, textAlign: 'center', left: 0, right: 0}}>
                            {t(TK.storageOfYourAccountIsFullWarning)}
                        </span>
                    : undefined
                }

                <Container>

                    <NavbarBrand id="appointmint-icon" tag={Link} to={paths.root()} onClick={homeClick}>
                        <AppNameIcon className="appNameIcon"/>
                        <AppIcon className="appIcon"/>
                    </NavbarBrand>

                    {features.includes(FeatureEnum.Branches) 
                        ? <AutocompleteTextField 
                            style={{flexGrow: 1000}}
                            inputStyle={{color: 'white'}}
                            value={settings.general?.branches?.[selectedBranch ?? 0]?.displayName}
                            disableClearable
                            options={Object.values(settings.general?.branches ?? {}).map(b => b.displayName)}
                            onValueChanged={onBranchChange}
                            variant='nobox'
                        />
                        : <div style={{flexGrow: 1000}}/>
                    }

                    <div style={{display: 'flex', gap: 'var(--spacer-XXS)', marginRight: 'var(--spacer-S)'}}>
                        {hasClientsChat && <MessagesMenu />}
                        {canSeeTasksMenu && <TasksMenu />}
                        {canSeeCashRegisterMenu && <CashRegisterMenu />}
                        <NotificationMenu />
                    </div>

                    <NavbarToggler  onClick={() => setIsOpen(!isOpen)} className="mr-2 togglerButton"/>
                    <Collapse className="d-sm-inline-flex flex-sm-row-reverse" style={{marginLeft: 'var(--spacer-S)'}} isOpen={isOpen} navbar>
                        <ul className="navbar-nav flex-grow" onClick={() => setIsOpen(false)}>
                            { 
                                !isLoggedIn && (
                                    <MyNavItem id="login" href={paths.login()} text={t(TK.login)} forceWhite/>
                                )
                            }
                            {
                                isLoggedIn && (
                                    <>
                                        <RequirePermission>
                                            <MyNavItem id="clients" href={paths.clients()} text={t(TK.clients)} forceWhite/>
                                        </RequirePermission>

                                        <RequireProfessionalsManagement>
                                            <MyNavItem id="professionals" href={paths.professionals()} text={t(TK.professionals)} forceWhite/>
                                        </RequireProfessionalsManagement>

                                        <Dropdown nav inNavbar
                                            id="user-dropdown"
                                            isOpen={userDropdownOpen || isOpen}
                                            toggle={userDropdownToggle}
                                            onMouseEnter={() => {
                                                setUserDropdownHovered(true);
                                                setUserDropdown(true);
                                            }}
                                            onMouseLeave={() => {
                                                setUserDropdownHovered(false);
                                                setUserDropdown(false);
                                            }}
                                        >
                                            <DropdownToggle nav caret className="usernameToggle">
                                                {showProfileImage && profileImageUrl && (
                                                    <Avatar
                                                        src={profileImageUrl}
                                                        alt={user?.name || user?.username}
                                                        imgProps={{ style: { objectFit: 'cover', objectPosition: 'top' }, onError: () => setShowProfileImage(false) }}
                                                        style={{
                                                            width: '28px',
                                                            height: '28px',
                                                            marginRight: 'var(--spacer-XS)',
                                                            display: 'inline-block',
                                                            verticalAlign: 'middle',
                                                            backgroundColor: 'transparent',
                                                            borderRadius: '6px',
                                                        }}
                                                    />
                                                )}
                                                {user?.name || user?.username}
                                            </DropdownToggle>

                                            <DropdownMenu right style={{transform: 'translateY(-10px)'}} onClick={() => setUserDropdown(false)}>

                                                {features.includes(FeatureEnum.ProfessionalPacks) && isProfessional &&
                                                    <DropdownItem>
                                                        <MyNavItem id="my-packs" href={paths.myPacks()} text={t(TK.myPacks)} icon={ListIcon} />
                                                    </DropdownItem>
                                                }

                                                <DropdownItem>
                                                    <MyNavItem id="profile" href={paths.profile()} text={t(TK.profile)} icon={AccountCircle} />
                                                </DropdownItem>

                                                {features.includes(FeatureEnum.CRM) &&
                                                    <RequireCampaignManagement>
                                                        <DropdownItem>
                                                            <MyNavItem id="campaigns" href={paths.campaigns()} text={t(TK.campaigns)} icon={CampaignsIcon} />
                                                        </DropdownItem>
                                                    </RequireCampaignManagement>
                                                }

                                                {features.includes(FeatureEnum.Analytics) &&
                                                    <RequireAnalyticsPermission>
                                                        <DropdownItem>
                                                            <MyNavItem id="analytics" href={paths.analytics()} text={t(TK.analytics)} icon={DashboardIcon} />
                                                        </DropdownItem>
                                                    </RequireAnalyticsPermission>
                                                }

                                                { features.includes(FeatureEnum.Stock) &&
                                                    <RequirePricesManagement>
                                                        <DropdownItem>
                                                            <MyNavItem id="stock" href={paths.stock()} text={t(TK.stock)} icon={InventoryIcon}/>
                                                        </DropdownItem>
                                                    </RequirePricesManagement>
                                                }

                                                <RequirePermission>
                                                    <DropdownItem>
                                                        <MyNavItem id="waiting-lists" href={paths.waitingLists()} text={t(TK.waitingLists)} icon={FormatListNumberedIcon} />
                                                    </DropdownItem>
                                                </RequirePermission>

                                                {features.includes(FeatureEnum.Billing) &&
                                                    <RequireAppointmentsManagement>
                                                        <DropdownItem>
                                                            <MyNavItem id="billing" href={paths.billing()} text={t(TK.billing)} icon={LocalAtmIcon} />
                                                        </DropdownItem>
                                                    </RequireAppointmentsManagement>
                                                }

                                                {settings.cashRegisterSettings?.enabled &&
                                                    <DropdownItem>
                                                        <MyNavItem id="cash-register" href={paths.cashRegister()} text={t(TK.cashRegister)} icon={MonetizationOnIcon} />
                                                    </DropdownItem>
                                                }

                                                <RequireAdministrator>
                                                    <DropdownItem>
                                                        <MyNavItem id="settings" href={paths.settings()} text={t(TK.settings)} icon={Settings} />
                                                    </DropdownItem>
                                                </RequireAdministrator>

                                                <DropdownItem divider />

                                                <DropdownItem onClick={logout}>
                                                    <MyNavItem id="logout" href={paths.root()} text={t(TK.logout)} icon={ExitToApp} />
                                                </DropdownItem>

                                            </DropdownMenu>
                                        </Dropdown>
                                    </>
                                )
                            }
                        </ul>
                    </Collapse>
                </Container>
            </Navbar>
        </header>
    );
}

export default NavMenu;
